import React, { useState, useEffect, useCallback } from 'react';
import { Briefcase, Calendar, ChevronLeft, ChevronRight, AlertTriangle, Tag, Plus, FileText, PoundSterling } from 'lucide-react';
import appointmentService from '../services/appointmentService';
import { getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import TechnicianFilter from './TechnicianFilter';
import useMobileHeaderActions from '../hooks/useMobileHeaderActions';
import MobileFAB from './MobileFAB';
import AppointmentTimeline from './AppointmentTimeline';

const Dashboard = ({ jobs, onNavigate, user, userProfile, onAddJob, onAddContract }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [overdueCount, setOverdueCount] = useState(0);
  const techFilter = useTechnicianFilter(userProfile);
  const filteredFranchiseeId = techFilter.showFilter ? techFilter.getFilteredFranchiseeId() : null;
  const selectedBranchId = techFilter.getSelectedBranchId();

  useMobileHeaderActions(
    techFilter.showFilter ? <TechnicianFilter {...techFilter} compact /> : null,
    [techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  // Filter jobs by selected franchisee and/or branch
  const scopedJobs = techFilter.selectedTechnician === 'all' ? jobs : jobs.filter(job => {
    if (filteredFranchiseeId && filteredFranchiseeId !== 'all' && job.franchisee_id !== filteredFranchiseeId) return false;
    if (selectedBranchId && job.branch_id !== selectedBranchId) return false;
    return true;
  });

  const activeJobs = scopedJobs.filter(job => job.status === 'active');
  const activeNonContracts = activeJobs.filter(job => !job.is_contract);
  const unscheduledNonContracts = activeNonContracts.filter(job =>
    !job.next_appointment_date || new Date(job.next_appointment_date) < new Date()
  );

  // Contract stats
  const activeContracts = activeJobs.filter(job => job.is_contract);
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const newContractsThisMonth = activeContracts.filter(job => new Date(job.created_at) >= startOfMonth);
  const unscheduledContracts = activeContracts.filter(job =>
    !job.next_appointment_date || new Date(job.next_appointment_date) < now
  );

  // Contracts won stats (all contracts, not just active)
  const allContracts = scopedJobs.filter(job => job.is_contract);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  const contractsWonThisMonth = allContracts.filter(job => new Date(job.created_at) >= startOfMonth);
  const contractsWonLastMonth = allContracts.filter(job => {
    const d = new Date(job.created_at);
    return d >= startOfLastMonth && d <= endOfLastMonth;
  });
  const contractsWonThisMonthValue = contractsWonThisMonth.reduce((sum, job) => sum + (parseFloat(job.price) || 0), 0);
  const contractsWonLastMonthValue = contractsWonLastMonth.reduce((sum, job) => sum + (parseFloat(job.price) || 0), 0);
  const allContractsValue = allContracts.reduce((sum, job) => sum + (parseFloat(job.price) || 0), 0);
  const isAdminOrFranchisee = user?.role === 'admin' || user?.role === 'franchisee';

  // Format date for display
  const formatDisplayDate = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isSameDay = (a, b) => a.toDateString() === b.toDateString();

    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, tomorrow)) return 'Tomorrow';
    if (isSameDay(date, yesterday)) return 'Yesterday';

    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const dateString = selectedDate.toISOString().split('T')[0];

  // Load appointments for selected date
  const loadAppointments = useCallback(async () => {
    const currentUser = userProfile || user;
    if (!currentUser) return;

    try {
      setLoadingAppointments(true);
      const franchiseeId = filteredFranchiseeId || getFranchiseeIdForDataLoading(currentUser);
      if (!franchiseeId) return;

      const params = {
        date_from: dateString,
        date_to: dateString,
        sortBy: 'scheduled_date',
        sortOrder: 'ASC',
        limit: 50
      };
      if (selectedBranchId) params.branch_id = selectedBranchId;

      const response = await appointmentService.getAppointments(franchiseeId, params);
      setAppointments(response.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  }, [dateString, user, userProfile, filteredFranchiseeId, selectedBranchId]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Fetch overdue appointment count
  useEffect(() => {
    const fetchOverdueCount = async () => {
      const currentUser = userProfile || user;
      if (!currentUser) return;
      try {
        const franchiseeId = filteredFranchiseeId || getFranchiseeIdForDataLoading(currentUser);
        if (!franchiseeId) return;
        const counts = await appointmentService.getAppointmentCounts(franchiseeId);
        setOverdueCount(counts.overdue || 0);
      } catch (error) {
        console.error('Error fetching overdue count:', error);
      }
    };
    fetchOverdueCount();
  }, [user, userProfile, filteredFranchiseeId]);

  const goToday = () => setSelectedDate(new Date());
  const goPrevDay = () => setSelectedDate(prev => {
    const d = new Date(prev);
    d.setDate(d.getDate() - 1);
    return d;
  });
  const goNextDay = () => setSelectedDate(prev => {
    const d = new Date(prev);
    d.setDate(d.getDate() + 1);
    return d;
  });

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  // Format time from datetime string
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };


  return (
    <div className="px-2 py-2 md:p-6">
      {/* Desktop Layout */}
      <div className="hidden md:flex md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onAddJob} className="btn btn-md btn-secondary">
            <Plus size={15} />
            <span>Add Job</span>
          </button>
          <button onClick={onAddContract} className="btn btn-md btn-secondary">
            <Plus size={15} />
            <span>Add Contract</span>
          </button>
          <TechnicianFilter {...techFilter} />
        </div>
      </div>

      <MobileFAB onAddJob={onAddJob} onAddContract={onAddContract} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Active Jobs */}
        <div
          onClick={() => onNavigate('jobs')}
          className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Active Jobs</p>
              <p className="text-3xl font-bold text-gray-900">{activeNonContracts.length}</p>
            </div>
            <div className="bg-pest-green p-3 rounded-full">
              <Briefcase className="text-white" size={24} />
            </div>
          </div>
          {unscheduledNonContracts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate('jobs', null, { initialFilter: 'unscheduled_jobs' });
                }}
                className="flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
              >
                <AlertTriangle size={16} />
                <span>{unscheduledNonContracts.length} with no scheduled appointments</span>
                <span className="text-amber-500 underline ml-1">Schedule now</span>
              </button>
            </div>
          )}
        </div>

        {/* Today's Appointments count */}
        <div
          onClick={() => onNavigate('calendar')}
          className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Today's Appointments</p>
              <p className="text-3xl font-bold text-gray-900">
                {isToday ? appointments.length : '—'}
              </p>
            </div>
            <div className="bg-pest-blue p-3 rounded-full">
              <Calendar className="text-white" size={24} />
            </div>
          </div>
          {overdueCount > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate('appointments', null, { initialFilter: 'overdue' });
                }}
                className="flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
              >
                <AlertTriangle size={16} />
                <span>{overdueCount} past appointments not completed</span>
                <span className="text-amber-500 underline ml-1">Fix now</span>
              </button>
            </div>
          )}
        </div>

        {/* Active Contracts */}
        <div
          onClick={() => onNavigate('jobs')}
          className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Active Contracts</p>
              <p className="text-3xl font-bold text-gray-900">
                {activeContracts.length}
                {newContractsThisMonth.length > 0 && (
                  <span className="text-sm font-medium text-green-600 ml-2">
                    ({newContractsThisMonth.length} new this month)
                  </span>
                )}
              </p>
            </div>
            <div className="bg-green-600 p-3 rounded-full">
              <Tag className="text-white" size={24} />
            </div>
          </div>
          {unscheduledContracts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate('jobs', null, { initialFilter: 'unscheduled_contracts' });
                }}
                className="flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
              >
                <AlertTriangle size={16} />
                <span>{unscheduledContracts.length} with no scheduled appointments</span>
                <span className="text-amber-500 underline ml-1">Schedule now</span>
              </button>
            </div>
          )}
        </div>

        {/* Contracts Won */}
        <div
          onClick={() => onNavigate('jobs')}
          className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Contracts Won</p>
              <p className="text-3xl font-bold text-gray-900">{allContracts.length}</p>
            </div>
            <div className="bg-purple-600 p-3 rounded-full">
              <PoundSterling className="text-white" size={24} />
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
            {isAdminOrFranchisee && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Total value</span>
                <span className="font-semibold text-gray-900">
                  £{allContractsValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">This month</span>
              <span className="font-semibold text-gray-900">
                {contractsWonThisMonth.length} &middot; £{contractsWonThisMonthValue.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Last month</span>
              <span className="font-semibold text-gray-900">
                {contractsWonLastMonth.length} &middot; £{contractsWonLastMonthValue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Appointments for selected day */}
      <div className="bg-white md:rounded-lg md:shadow-md p-3 sm:p-6">
        {/* Date Navigation Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Appointments</h2>
            <span className="bg-pest-blue text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
              {loadingAppointments ? '...' : appointments.length}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              onClick={goPrevDay}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-medium text-gray-700 text-center">
              {formatDisplayDate(selectedDate)}
            </span>
            <button
              onClick={goNextDay}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <ChevronRight size={18} />
            </button>
            {!isToday && (
              <button
                onClick={goToday}
                className="text-xs text-pest-blue hover:text-pest-blue/80 font-medium px-2 py-1 rounded hover:bg-blue-50 ml-1"
              >
                Today
              </button>
            )}
          </div>
        </div>

        {/* Appointments List */}
        {loadingAppointments ? (
          <div className="text-gray-500 text-center py-8">Loading appointments...</div>
        ) : appointments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No appointments for {formatDisplayDate(selectedDate).toLowerCase()}</p>
        ) : (
          <AppointmentTimeline
            appointments={appointments}
            showDateGroups={false}
            onClick={(apt) => {
              if (apt.job_id) {
                const jobData = jobs.find(j => j.id === apt.job_id) || {
                  id: apt.job_id,
                  title: apt.job_title,
                  customer_id: apt.customer_id,
                  customer_name: apt.customer_name
                };
                onNavigate('job-details', jobData);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
